import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Box,
    LinearProgress,
    Avatar,
    Chip,
    Paper,
    Badge
} from '@mui/material';
import {
    EmojiEvents,
    Star,
    FlashOn,
    Psychology,
    SportsEsports,
    Lock,
    ArrowForward
} from '@mui/icons-material';

const StudentDashboardV2: React.FC = () => {
    const navigate = useNavigate();

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>

            {/* Gamified Header */}
            <Box sx={{
                mb: 5,
                p: 4,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #6C63FF 0%, #3F3D56 100%)',
                color: 'white',
                boxShadow: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={<Avatar sx={{ width: 32, height: 32, bgcolor: '#FFD700', border: '2px solid white' }}>⚡</Avatar>}
                    >
                        <Avatar sx={{ width: 80, height: 80, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 40 }}>👾</Avatar>
                    </Badge>
                    <Box>
                        <Typography variant="h4" fontWeight="900" sx={{ letterSpacing: 1 }}>
                            LEVEL 5 SCHOLAR
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Alex • 12,450 XP
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                            <LinearProgress variant="determinate" value={70} sx={{ width: 150, height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.2)', '& .MuiLinearProgress-bar': { bgcolor: '#FFD700' } }} />
                            <Typography variant="caption" fontWeight="bold">350 XP to Level 6</Typography>
                        </Box>
                    </Box>
                </Box>
                <Button variant="contained" sx={{ bgcolor: 'white', color: '#6C63FF', fontWeight: 'bold', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}>
                    View Rewards
                </Button>
            </Box>

            {/* Quest Board */}
            <Typography variant="h5" fontWeight="900" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#3F3D56' }}>
                <SportsEsports /> DAILY QUESTS
            </Typography>

            <Grid container spacing={3} sx={{ mb: 5 }}>
                {[
                    { title: 'Math Warrior', desc: 'Complete 3 Math Quizzes', progress: 1, total: 3, xp: 500, icon: <Psychology />, color: '#FF6B6B' },
                    { title: 'Speed Reader', desc: 'Read 2 Chapters of Literature', progress: 2, total: 2, xp: 300, icon: <MenuBook />, color: '#4ECDC4' },
                    { title: 'Streak Master', desc: 'Login for 7 days in a row', progress: 5, total: 7, xp: 1000, icon: <FlashOn />, color: '#FFE66D' }
                ].map((quest, index) => (
                    <Grid item xs={12} md={4} key={index}>
                        <Card sx={{ borderRadius: 4, height: '100%', position: 'relative', overflow: 'visible', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-5px)' } }}>
                            <Box sx={{ position: 'absolute', top: -15, right: -15, transform: 'rotate(15deg)' }}>
                                <Chip label={`+${quest.xp} XP`} sx={{ bgcolor: quest.color, color: 'white', fontWeight: 'bold', boxShadow: 2 }} />
                            </Box>
                            <CardContent sx={{ pt: 4, textAlign: 'center' }}>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: `${quest.color}20`, color: quest.color, mx: 'auto', mb: 2 }}>
                                    {quest.icon}
                                </Avatar>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>{quest.title}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{quest.desc}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                                    <LinearProgress variant="determinate" value={(quest.progress / quest.total) * 100} sx={{ width: '60%', height: 8, borderRadius: 4, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { bgcolor: quest.color } }} />
                                    <Typography variant="caption" fontWeight="bold">{quest.progress}/{quest.total}</Typography>
                                </Box>
                                {quest.progress === quest.total && (
                                    <Button size="small" variant="contained" sx={{ mt: 2, bgcolor: quest.color, borderRadius: 2 }}>Collect Reward</Button>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Course Map (Islands) */}
            <Typography variant="h5" fontWeight="900" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, color: '#3F3D56' }}>
                <Map /> ADVENTURE MAP (COURSES)
            </Typography>

            <Grid container spacing={3}>
                {[
                    { name: 'Algebra Island', level: 12, status: 'unlocked', color: '#FF6B6B' },
                    { name: 'Physics Peak', level: 8, status: 'unlocked', color: '#4ECDC4' },
                    { name: 'History Harbor', level: 5, status: 'locked', color: 'grey.300' }
                ].map((island, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                        <Card sx={{
                            borderRadius: 4,
                            height: 180,
                            bgcolor: island.status === 'locked' ? 'grey.100' : `${island.color}15`,
                            border: `2px solid ${island.status === 'locked' ? 'transparent' : island.color}`,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: island.status === 'locked' ? 'default' : 'pointer',
                            opacity: island.status === 'locked' ? 0.7 : 1
                        }} onClick={() => island.status !== 'locked' && navigate('/student-courses')}>
                            {island.status === 'locked' ? <Lock sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} /> : <Star sx={{ fontSize: 40, color: island.color, mb: 1 }} />}
                            <Typography variant="h6" fontWeight="bold" color={island.status === 'locked' ? 'text.secondary' : 'text.primary'}>
                                {island.name}
                            </Typography>
                            <Typography variant="caption" fontWeight="bold" sx={{ color: island.status === 'locked' ? 'text.secondary' : island.color }}>
                                {island.status === 'locked' ? 'Unlock at Level 10' : `Level ${island.level}`}
                            </Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>

        </Container>
    );
};
import { Map, MenuBook } from '@mui/icons-material'; // Late import fix
export default StudentDashboardV2;
